import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, LayoutDashboard, BarChart3, Image as ImageIcon, Layers, Shield, Calendar, Users, Repeat, Gift, UserPlus, History, Settings, Search, Plus, MoreVertical, ArrowUpRight, ArrowDownRight, Filter, Download, CheckCircle2, AlertCircle, KeyRound, Target, Crown, Edit3, Copy, Trash2, Sparkles, Video, MapPin, Flag, Eye, TrendingUp, Save, RotateCcw, ArrowRight, Map as MapIcon } from 'lucide-react';
interface TreasuryAdminDashboardProps {
  onClose: () => void;
  /**
   * When true, renders inline (no fixed overlay, no topbar) so the admin
   * dashboard slots into a parent container like the member dashboard preview.
   */
  inline?: boolean;
}
// ============================================================
// Mock data
// ============================================================
const ACTIVITY_30D = [18, 22, 25, 21, 28, 32, 30, 36, 41, 38, 44, 49, 46, 52, 58, 55, 61, 64, 60, 68, 72, 70, 75, 78, 74, 82, 86, 84, 91, 95];
const OPENRATE_14D = [62, 65, 64, 68, 71, 70, 74, 72, 75, 78, 77, 80, 82, 85];
const TOP_MISLS = [{
  name: 'Sukerchakia',
  category: 'Historic Prestige',
  members: 1520,
  xp: '480,210'
}, {
  name: 'Toronto Diaspora',
  category: 'Diaspora / City',
  members: 1042,
  xp: '312,840'
}, {
  name: 'Ahluwalia',
  category: 'Historic Prestige',
  members: 980,
  xp: '298,150'
}, {
  name: 'Majha Punjab',
  category: 'Homeland / Region',
  members: 845,
  xp: '241,300'
}, {
  name: 'Sandhu',
  category: 'Family / Clan',
  members: 712,
  xp: '198,720'
}];
const MISL_CATEGORIES: Record<string, {
  color: string;
  misls: {
    name: string;
    region: string;
    members: number;
    xp: string;
    misldar: string;
  }[];
}> = {
  'Historic Prestige': {
    color: '#D4AF37',
    misls: [{
      name: 'Sukerchakia',
      region: 'Gujranwala',
      members: 1520,
      xp: '480,210',
      misldar: '@ranjit_s'
    }, {
      name: 'Ahluwalia',
      region: 'Kapurthala',
      members: 980,
      xp: '298,150',
      misldar: '@jassa_a'
    }, {
      name: 'Bhangi',
      region: 'Lahore',
      members: 842,
      xp: '254,820',
      misldar: '@hari_b'
    }, {
      name: 'Kanhaiya',
      region: 'Sohian',
      members: 720,
      xp: '210,560',
      misldar: '@jai_k'
    }, {
      name: 'Ramgarhia',
      region: 'Amritsar',
      members: 695,
      xp: '198,440',
      misldar: '@nodh_r'
    }]
  },
  'Homeland / Region': {
    color: '#8B0000',
    misls: [{
      name: 'Majha Punjab',
      region: 'Amritsar Belt',
      members: 845,
      xp: '241,300',
      misldar: '@manjit_s'
    }, {
      name: 'Doaba Punjab',
      region: 'Jalandhar Belt',
      members: 730,
      xp: '208,910',
      misldar: '@parmjit_k'
    }, {
      name: 'Malwa Punjab',
      region: 'Patiala Belt',
      members: 680,
      xp: '189,420',
      misldar: '@balraj_s'
    }, {
      name: 'Pothohar',
      region: 'Rawalpindi',
      members: 412,
      xp: '112,380',
      misldar: '@gurnam_s'
    }, {
      name: 'Lahore Heartland',
      region: 'Lahore',
      members: 388,
      xp: '101,210',
      misldar: '@deep_l'
    }]
  },
  'Diaspora / City': {
    color: '#D4AF37',
    misls: [{
      name: 'Toronto Diaspora',
      region: 'GTA, Canada',
      members: 1042,
      xp: '312,840',
      misldar: '@harnoor_s'
    }, {
      name: 'Vancouver Diaspora',
      region: 'BC, Canada',
      members: 820,
      xp: '241,520',
      misldar: '@arvind_k'
    }, {
      name: 'London Diaspora',
      region: 'UK',
      members: 745,
      xp: '218,610',
      misldar: '@simran_b'
    }, {
      name: 'NYC Diaspora',
      region: 'New York, USA',
      members: 612,
      xp: '178,950',
      misldar: '@mehar_s'
    }, {
      name: 'Sydney Diaspora',
      region: 'NSW, Australia',
      members: 410,
      xp: '118,720',
      misldar: '@karam_s'
    }]
  },
  'Family / Clan': {
    color: '#8B0000',
    misls: [{
      name: 'Sandhu',
      region: 'Pan-Punjab',
      members: 712,
      xp: '198,720',
      misldar: '@gurdeep_s'
    }, {
      name: 'Sidhu',
      region: 'Malwa',
      members: 690,
      xp: '188,310',
      misldar: '@navjot_s'
    }, {
      name: 'Dhillon',
      region: 'Majha',
      members: 580,
      xp: '161,540',
      misldar: '@param_d'
    }, {
      name: 'Gill',
      region: 'Doaba & Malwa',
      members: 540,
      xp: '152,820',
      misldar: '@harman_g'
    }, {
      name: 'Brar',
      region: 'Faridkot Belt',
      members: 460,
      xp: '128,940',
      misldar: '@jaspal_b'
    }]
  }
};
const MEMBERS = [{
  name: 'Harnoor S.',
  handle: '@harnoor_s',
  rank: 'Sardar',
  misl: 'Sukerchakia',
  xp: '4,150',
  joined: 'Oct 12, 2023',
  status: 'Active'
}, {
  name: 'Ranjit S.',
  handle: '@ranjit_s',
  rank: 'Misldar',
  misl: 'Sukerchakia',
  xp: '12,400',
  joined: 'Sep 01, 2023',
  status: 'Active'
}, {
  name: 'Amrit K.',
  handle: '@amrit_k',
  rank: 'Courtier',
  misl: 'Ahluwalia',
  xp: '1,250',
  joined: 'Nov 05, 2023',
  status: 'Active'
}, {
  name: 'Dilraj S.',
  handle: '@dilraj_s',
  rank: 'Sardar',
  misl: 'Ramgarhia',
  xp: '6,910',
  joined: 'Sep 15, 2023',
  status: 'Active'
}, {
  name: 'Simran K.',
  handle: '@simran_k',
  rank: 'Sardar',
  misl: 'Toronto Diaspora',
  xp: '7,640',
  joined: 'Aug 22, 2023',
  status: 'Active'
}, {
  name: 'Jaspreet S.',
  handle: '@jas_s',
  rank: 'Courtier',
  misl: 'Sandhu',
  xp: '980',
  joined: 'Dec 02, 2023',
  status: 'Flagged'
}, {
  name: 'Manpreet K.',
  handle: '@manpreet_k',
  rank: 'Sardar',
  misl: 'London Diaspora',
  xp: '5,420',
  joined: 'Oct 28, 2023',
  status: 'Active'
}, {
  name: 'Arjan S.',
  handle: '@arjan_s',
  rank: 'Sardar',
  misl: 'Sukerchakia',
  xp: '8,150',
  joined: 'Sep 19, 2023',
  status: 'Active'
}];
const CODES = [{
  code: 'COURT-AWAKEN-7B',
  event: 'The Court Awakens — Opening Preview',
  xp: 200,
  used: 142,
  total: 200,
  expires: 'Jul 19, 2026',
  status: 'Active'
}, {
  code: 'SCENT-SONG-2C',
  event: 'The Chamber of Scent & Song',
  xp: 200,
  used: 0,
  total: 200,
  expires: 'Aug 16, 2026',
  status: 'Scheduled'
}, {
  code: 'AI-ARTISTS-9X',
  event: 'The AI Artists Behind the Faces',
  xp: 200,
  used: 38,
  total: 500,
  expires: 'Jul 26, 2026',
  status: 'Active'
}, {
  code: 'REGALIA-4K',
  event: 'The Royal Regalia Reveal',
  xp: 200,
  used: 0,
  total: 300,
  expires: 'Sep 20, 2026',
  status: 'Scheduled'
}, {
  code: 'FOUNDERS-FEAST-1',
  event: "Founders' Feast",
  xp: 500,
  used: 0,
  total: 100,
  expires: 'Oct 18, 2026',
  status: 'Scheduled'
}, {
  code: 'IMMERSIVE-BTS-3',
  event: 'Building the Darbar — Immersive Team',
  xp: 200,
  used: 21,
  total: 500,
  expires: 'Aug 30, 2026',
  status: 'Active'
}, {
  code: 'WELCOME-FOUNDER',
  event: 'Onboarding Welcome',
  xp: 100,
  used: 1820,
  total: 5000,
  expires: 'Dec 31, 2026',
  status: 'Active'
}, {
  code: 'PREVIEW-EXPIRED-1',
  event: 'Closed Court Preview (test)',
  xp: 100,
  used: 50,
  total: 50,
  expires: 'May 01, 2026',
  status: 'Expired'
}];
const RANJIT_IMG = 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png';
const JIND_IMG = 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png';
const NALWA_IMG = 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png';
const MORAN_IMG = 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png';
const ARTIFACTS = [{
  name: 'Maharaja Ranjit Singh',
  img: RANJIT_IMG,
  rarity: "Founders' Edition",
  set: 'Lahore Court',
  supply: 500,
  minted: 500,
  status: 'Live'
}, {
  name: 'Maharani Jind Kaur',
  img: JIND_IMG,
  rarity: 'Epic',
  set: 'Lahore Court',
  supply: 1000,
  minted: 450,
  status: 'Live'
}, {
  name: 'Hari Singh Nalwa',
  img: NALWA_IMG,
  rarity: 'Epic',
  set: 'Generals',
  supply: 1000,
  minted: 890,
  status: 'Live'
}, {
  name: 'Koh-i-Noor Diamond',
  img: RANJIT_IMG,
  rarity: 'Treasury Rare',
  set: 'Royal Treasury',
  supply: 150,
  minted: 120,
  status: 'Live'
}, {
  name: 'Golden Throne of Lahore',
  img: RANJIT_IMG,
  rarity: 'Treasury Rare',
  set: 'Royal Treasury',
  supply: 200,
  minted: 0,
  status: 'Draft'
}, {
  name: 'Royal Seal of Ranjit Singh',
  img: RANJIT_IMG,
  rarity: 'Treasury Rare',
  set: "Founders' Seals",
  supply: 500,
  minted: 412,
  status: 'Live'
}, {
  name: 'Akali Phula Singh',
  img: NALWA_IMG,
  rarity: 'Epic',
  set: 'Generals',
  supply: 1000,
  minted: 720,
  status: 'Live'
}, {
  name: 'Moran Sarkar',
  img: MORAN_IMG,
  rarity: 'Epic',
  set: 'Inner Life',
  supply: 1000,
  minted: 540,
  status: 'Live'
}, {
  name: 'Battle of Attock',
  img: NALWA_IMG,
  rarity: 'Treasury Rare',
  set: 'Defining Battles',
  supply: 250,
  minted: 0,
  status: 'Scheduled'
}, {
  name: 'Gurdwara Janam Asthan',
  img: RANJIT_IMG,
  rarity: 'Epic',
  set: 'Architecture',
  supply: 800,
  minted: 0,
  status: 'Draft'
}];
const COLLECTIONS = [{
  name: 'The Lahore Court',
  cards: 8,
  complete: 8,
  released: 'Mar 2024',
  status: 'Live',
  rarity: 'Mixed'
}, {
  name: "Founders' Seals",
  cards: 6,
  complete: 6,
  released: 'Mar 2024',
  status: 'Live',
  rarity: 'Treasury Rare'
}, {
  name: 'Generals of the Khalsa',
  cards: 10,
  complete: 9,
  released: 'May 2024',
  status: 'Live',
  rarity: 'Epic'
}, {
  name: 'Royal Treasury',
  cards: 8,
  complete: 4,
  released: 'Jul 2024',
  status: 'Live',
  rarity: 'Treasury Rare'
}, {
  name: 'Inner Life of the Court',
  cards: 12,
  complete: 7,
  released: 'Sep 2024',
  status: 'Live',
  rarity: 'Epic'
}, {
  name: 'Sons of the Sarkar',
  cards: 6,
  complete: 6,
  released: 'Nov 2024',
  status: 'Live',
  rarity: 'Epic'
}, {
  name: 'Defining Battles',
  cards: 10,
  complete: 0,
  released: 'Q3 2026',
  status: 'Scheduled',
  rarity: 'Treasury Rare'
}, {
  name: 'Architecture of the Empire',
  cards: 8,
  complete: 0,
  released: 'Q4 2026',
  status: 'Draft',
  rarity: 'Epic'
}];
const EVENTS_NIGHTS = [{
  date: 'Jul 18, 2026',
  title: 'The Court Awakens — Opening Preview',
  location: 'Illuminarium · Toronto',
  rank: 'All Ranks',
  code: 'COURT-AWAKEN-7B',
  attended: 142,
  capacity: 200,
  status: 'Active'
}, {
  date: 'Aug 15, 2026',
  title: 'The Chamber of Scent & Song',
  location: 'Illuminarium · Toronto',
  rank: 'All Ranks',
  code: 'SCENT-SONG-2C',
  attended: 0,
  capacity: 200,
  status: 'Scheduled'
}, {
  date: 'Sep 19, 2026',
  title: 'The Royal Regalia Reveal',
  location: 'Illuminarium · Toronto',
  rank: 'Courtier+',
  code: 'REGALIA-4K',
  attended: 0,
  capacity: 300,
  status: 'Scheduled'
}, {
  date: 'Oct 17, 2026',
  title: "Founders' Feast",
  location: 'Illuminarium · Toronto',
  rank: 'Sardar+',
  code: 'FOUNDERS-FEAST-1',
  attended: 0,
  capacity: 100,
  status: 'Scheduled'
}];
const EVENTS_SESSIONS = [{
  date: 'Jul 25, 2026',
  title: 'The AI Artists Behind the Faces',
  location: 'Virtual · Court Channel',
  rank: 'All Ranks',
  code: 'AI-ARTISTS-9X',
  attended: 38,
  capacity: 500,
  status: 'Active'
}, {
  date: 'Aug 29, 2026',
  title: 'Building the Darbar — Immersive Team',
  location: 'Virtual · Court Channel',
  rank: 'Courtier+',
  code: 'IMMERSIVE-BTS-3',
  attended: 21,
  capacity: 500,
  status: 'Active'
}, {
  date: 'Sep 26, 2026',
  title: 'The Sound of the Kingdom — Composing the Score',
  location: 'Virtual · Court Channel',
  rank: 'All Ranks',
  code: 'SOUND-KINGDOM-5',
  attended: 0,
  capacity: 500,
  status: 'Scheduled'
}, {
  date: 'Oct 24, 2026',
  title: 'Taste of Lahore — Royal Culinary Reveal',
  location: 'Virtual · Court Channel',
  rank: 'Sardar+',
  code: 'TASTE-LAHORE-8',
  attended: 0,
  capacity: 500,
  status: 'Scheduled'
}];
const TRADES_RECENT = [{
  id: 'TX-4821',
  from: '@ranjit_s',
  to: '@harnoor_s',
  artifact: 'Koh-i-Noor Diamond',
  value: '320 XP',
  fee: '1 XP',
  time: '2m ago',
  status: 'Completed'
}, {
  id: 'TX-4820',
  from: '@simran_k',
  to: '@arjan_s',
  artifact: 'Hari Singh Nalwa',
  value: '180 XP',
  fee: '1 XP',
  time: '14m ago',
  status: 'Completed'
}, {
  id: 'TX-4819',
  from: '@amrit_k',
  to: '@dilraj_s',
  artifact: 'Royal Seal of Ranjit Singh',
  value: '410 XP',
  fee: '1 XP',
  time: '48m ago',
  status: 'Completed'
}, {
  id: 'TX-4818',
  from: '@jas_s',
  to: '@manpreet_k',
  artifact: 'Maharani Jind Kaur',
  value: '240 XP',
  fee: '1 XP',
  time: '1h ago',
  status: 'Completed'
}];
const TRADES_FLAGGED = [{
  id: 'TX-4817',
  from: '@jas_s',
  to: '@new_user_x91',
  artifact: 'Maharaja Ranjit Singh',
  value: '5 XP',
  fee: '1 XP',
  time: '3h ago',
  reason: 'Suspicious price · same IP',
  status: 'Flagged'
}, {
  id: 'TX-4810',
  from: '@bot_acc_22',
  to: '@jas_s',
  artifact: 'Akali Phula Singh',
  value: '10 XP',
  fee: '1 XP',
  time: '6h ago',
  reason: 'Possible bot account',
  status: 'Flagged'
}, {
  id: 'TX-4805',
  from: '@new_user_x91',
  to: '@jas_s',
  artifact: 'Moran Sarkar',
  value: '8 XP',
  fee: '1 XP',
  time: '9h ago',
  reason: 'Reciprocal low-value trade',
  status: 'Flagged'
}];
const QUESTS = [{
  name: 'First Light',
  trigger: 'Open first Daily Packet',
  reward: '+100 XP',
  cadence: 'One-shot',
  completion: 96,
  status: 'Active'
}, {
  name: 'Daily Devotion',
  trigger: 'Open Daily Packet (7 days in a row)',
  reward: '+250 XP · Bonus Packet',
  cadence: 'Recurring',
  completion: 42,
  status: 'Active'
}, {
  name: 'The Trader',
  trigger: 'Complete 3 trades',
  reward: '+150 XP',
  cadence: 'Recurring',
  completion: 28,
  status: 'Active'
}, {
  name: 'Court Witness',
  trigger: 'Attend a Treasury Night',
  reward: '+200 XP · Live Attended Badge',
  cadence: 'One-shot',
  completion: 18,
  status: 'Active'
}, {
  name: "Founder's Decree",
  trigger: 'Invite 5 new members',
  reward: 'Founder Seal · +500 XP',
  cadence: 'One-shot',
  completion: 8,
  status: 'Active'
}, {
  name: 'Set Sealer',
  trigger: 'Complete any 6-card collection',
  reward: '+300 XP',
  cadence: 'Recurring',
  completion: 22,
  status: 'Active'
}, {
  name: 'The Pilgrim',
  trigger: 'Attend 3 Court Sessions',
  reward: '+400 XP · Pilgrim Sash',
  cadence: 'One-shot',
  completion: 4,
  status: 'Draft'
}];
const REFERRERS = [{
  rank: 1,
  name: 'Harnoor S.',
  handle: '@harnoor_s',
  invites: 47,
  signups: 32,
  xp: '+8,400'
}, {
  rank: 2,
  name: 'Ranjit S.',
  handle: '@ranjit_s',
  invites: 41,
  signups: 29,
  xp: '+7,250'
}, {
  rank: 3,
  name: 'Simran K.',
  handle: '@simran_k',
  invites: 38,
  signups: 24,
  xp: '+6,000'
}, {
  rank: 4,
  name: 'Dilraj S.',
  handle: '@dilraj_s',
  invites: 31,
  signups: 19,
  xp: '+4,750'
}, {
  rank: 5,
  name: 'Amrit K.',
  handle: '@amrit_k',
  invites: 28,
  signups: 17,
  xp: '+4,250'
}, {
  rank: 6,
  name: 'Manpreet K.',
  handle: '@manpreet_k',
  invites: 24,
  signups: 14,
  xp: '+3,500'
}, {
  rank: 7,
  name: 'Arjan S.',
  handle: '@arjan_s',
  invites: 21,
  signups: 12,
  xp: '+3,000'
}];
const AUDIT_LOG = [{
  ts: '2026-05-13 09:42:14',
  admin: '@harnoor_s',
  action: 'granted +500 XP to',
  target: '@harjeet_k',
  kind: 'XP Grant'
}, {
  ts: '2026-05-13 09:18:02',
  admin: '@harnoor_s',
  action: 'created event',
  target: "Founders' Feast",
  kind: 'Event'
}, {
  ts: '2026-05-13 08:51:30',
  admin: '@ranjit_s',
  action: 'archived artifact',
  target: 'Test Card v0.3',
  kind: 'Artifact'
}, {
  ts: '2026-05-12 22:04:11',
  admin: '@harnoor_s',
  action: 'set Misldar of Bhangi Misl to',
  target: '@hari_b',
  kind: 'Misl'
}, {
  ts: '2026-05-12 18:30:55',
  admin: '@ranjit_s',
  action: 'flagged trade',
  target: 'TX-4817',
  kind: 'Trade'
}, {
  ts: '2026-05-12 14:20:08',
  admin: '@harnoor_s',
  action: 'generated code',
  target: 'FOUNDERS-FEAST-1',
  kind: 'Code'
}, {
  ts: '2026-05-12 11:15:42',
  admin: '@harnoor_s',
  action: 'updated packet drop weights',
  target: 'Founders 1% / Rare 8%',
  kind: 'Settings'
}, {
  ts: '2026-05-11 20:08:19',
  admin: '@ranjit_s',
  action: 'banned account',
  target: '@bot_acc_22',
  kind: 'Member'
}, {
  ts: '2026-05-11 16:42:31',
  admin: '@harnoor_s',
  action: 'launched collection',
  target: 'Sons of the Sarkar',
  kind: 'Collection'
}, {
  ts: '2026-05-11 12:01:55',
  admin: '@harnoor_s',
  action: 'updated brand name to',
  target: 'Treasures of the Sikh Empire',
  kind: 'Settings'
}];
const TIER_REWARDS = [{
  count: 1,
  reward: '+500 XP',
  label: 'First invite bonus'
}, {
  count: 3,
  reward: 'Bonus Packet',
  label: 'A free Treasury packet'
}, {
  count: 5,
  reward: 'Founder Seal',
  label: 'A permanent identity seal'
}, {
  count: 10,
  reward: 'Live Night ticket',
  label: 'Reserved seat at Treasury Nights'
}];
// ============================================================
// Visual helpers
// ============================================================
const SectionHeader = ({
  title,
  eyebrow,
  action
}: {
  title: string;
  eyebrow: string;
  action?: React.ReactNode;
}) => <div className="flex flex-wrap gap-4 justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-2221">
    <div data-id="element-2222">
      <h2 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-2223">{title}</h2>
      <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-2224">
        {eyebrow}
      </p>
    </div>
    {action && <div className="flex flex-wrap gap-3 items-center" data-id="element-2225">{action}</div>}
  </div>;
const StatusPill = ({
  status
}: {
  status: string;
}) => {
  const map: Record<string, string> = {
    Active: 'text-emerald-400 border-emerald-400/40 bg-emerald-400/5',
    Live: 'text-emerald-400 border-emerald-400/40 bg-emerald-400/5',
    Completed: 'text-emerald-400 border-emerald-400/40 bg-emerald-400/5',
    Scheduled: 'text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/5',
    Draft: 'text-[#FDFBF7]/50 border-[#FDFBF7]/20 bg-[#FDFBF7]/5',
    Expired: 'text-[#FDFBF7]/35 border-[#FDFBF7]/15 bg-[#FDFBF7]/[0.02]',
    Flagged: 'text-red-400 border-red-400/40 bg-red-400/5',
    Banned: 'text-red-400 border-red-400/40 bg-red-400/5'
  };
  const cls = map[status] ?? 'text-[#FDFBF7]/60 border-[#FDFBF7]/20 bg-[#FDFBF7]/5';
  return <span className={`inline-flex items-center gap-1.5 text-[9px] uppercase tracking-widest border px-2 py-1 ${cls}`} data-id="element-2226">
      {(status === 'Active' || status === 'Live' || status === 'Completed') && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-2227" />}
      {status}
    </span>;
};
const RarityChip = ({
  rarity
}: {
  rarity: string;
}) => {
  let cls = 'border-[#FDFBF7]/20 text-[#FDFBF7]/60 bg-[#FDFBF7]/5';
  if (rarity.includes('Founders')) cls = 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10';else if (rarity.includes('Rare')) cls = 'border-[#D4AF37]/50 text-[#D4AF37]/80 bg-[#D4AF37]/5';else if (rarity.includes('Epic')) cls = 'border-[#8B0000] text-[#8B0000] bg-[#8B0000]/10';
  return <span className={`text-[9px] uppercase tracking-widest px-2 py-1 border whitespace-nowrap ${cls}`} data-id="element-2228">
      {rarity}
    </span>;
};
const KpiTile = ({
  label,
  value,
  trend,
  up
}: {
  label: string;
  value: string;
  trend?: string;
  up?: boolean;
}) => <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2229">
    <p className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest mb-2" data-id="element-2230">
      {label}
    </p>
    <div className="flex items-end justify-between" data-id="element-2231">
      <p className="font-serif text-2xl lg:text-3xl text-[#D4AF37]" data-id="element-2232">{value}</p>
      {trend && <p className={`text-[10px] flex items-center gap-1 ${up ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-2233">
          {up ? <ArrowUpRight className="w-3 h-3" data-id="element-2234" /> : <ArrowDownRight className="w-3 h-3" data-id="element-2235" />}
          {trend}
        </p>}
    </div>
  </div>;
// SVG line chart
const ActivityChart = ({
  data,
  height = 180
}: {
  data: number[];
  height?: number;
}) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const width = 800;
  const padding = 24;
  const stepX = (width - padding * 2) / (data.length - 1);
  const points = data.map((v, i) => {
    const x = padding + i * stepX;
    const y = padding + (1 - (v - min) / (max - min || 1)) * (height - padding * 2);
    return [x, y] as [number, number];
  });
  const linePath = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1][0]} ${height - padding} L ${points[0][0]} ${height - padding} Z`;
  return <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{
    height: `${height}px`
  }} preserveAspectRatio="none" data-id="element-2236">
      <defs data-id="element-2237">
        <linearGradient id="area-grad" x1="0" y1="0" x2="0" y2="1" data-id="element-2238">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.35" data-id="element-2239" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" data-id="element-2240" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map(g => <line key={g} x1={padding} x2={width - padding} y1={padding + g * (height - padding * 2)} y2={padding + g * (height - padding * 2)} stroke="#D4AF37" strokeOpacity="0.08" strokeDasharray="3 5" data-id="element-2241" />)}
      <path d={areaPath} fill="url(#area-grad)" data-id="element-2242" />
      <path d={linePath} fill="none" stroke="#D4AF37" strokeWidth="1.5" data-id="element-2243" />
      {points.map(([x, y], i) => i === points.length - 1 ? <circle key={i} cx={x} cy={y} r="3" fill="#D4AF37" data-id="element-2244" /> : null)}
    </svg>;
};
const Toggle = ({
  on,
  onChange
}: {
  on: boolean;
  onChange: () => void;
}) => <button type="button" onClick={onChange} className={`relative w-10 h-5 transition-colors border ${on ? 'bg-[#D4AF37]/30 border-[#D4AF37]' : 'bg-[#0B0F19] border-[#D4AF37]/20'}`} aria-pressed={on} data-id="element-2245">
    <span className={`absolute top-0.5 w-3.5 h-3.5 transition-transform ${on ? 'translate-x-5 bg-[#D4AF37]' : 'translate-x-0.5 bg-[#FDFBF7]/40'}`} data-id="element-2246" />
  </button>;
// ============================================================
// Main component
// ============================================================
export const TreasuryAdminDashboard: React.FC<TreasuryAdminDashboardProps> = ({
  onClose,
  inline = false
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [memberRankFilter, setMemberRankFilter] = useState('All');
  const [memberMislFilter, setMemberMislFilter] = useState('All');
  const [artifactRarityFilter, setArtifactRarityFilter] = useState('All');
  const [eventSubTab, setEventSubTab] = useState<'nights' | 'sessions'>('nights');
  const [tradeSubTab, setTradeSubTab] = useState<'recent' | 'flagged'>('recent');
  const [dropWeights, setDropWeights] = useState({
    founders: 1,
    rare: 8,
    epic: 22,
    standard: 69
  });
  const [featureFlags, setFeatureFlags] = useState({
    tradeMarket: true,
    courtSessions: true,
    mislSwitching: false,
    publicLeaderboard: true
  });
  const tabs = [{
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard
  }, {
    id: 'members',
    label: 'Members',
    icon: Users
  }, {
    id: 'misls',
    label: 'Misls',
    icon: Shield
  }, {
    id: 'codes',
    label: 'Codes',
    icon: KeyRound
  }, {
    id: 'collectibles',
    label: 'Collectibles',
    icon: ImageIcon
  }, {
    id: 'collections',
    label: 'Collections',
    icon: Layers
  }, {
    id: 'events',
    label: 'Live Events',
    icon: Calendar
  }, {
    id: 'trades',
    label: 'Trades',
    icon: Repeat
  }, {
    id: 'packets',
    label: 'Daily Packet',
    icon: Gift
  }, {
    id: 'restoration',
    label: 'Restoration',
    icon: MapIcon
  }, {
    id: 'quests',
    label: 'Quests',
    icon: Target
  }, {
    id: 'referrals',
    label: 'Referrals',
    icon: UserPlus
  }, {
    id: 'audit',
    label: 'Audit Log',
    icon: History
  }, {
    id: 'settings',
    label: 'Settings',
    icon: Settings
  }];
  const allMisls = Object.values(MISL_CATEGORIES).flatMap(c => c.misls.map(m => m.name));
  const ranks = ['Mildar', 'Misldar', 'Sardar', 'Courtier', 'Initiate'];
  const rarities = ["Founders' Edition", 'Treasury Rare', 'Epic'];
  const filteredMembers = MEMBERS.filter(m => {
    if (memberRankFilter !== 'All' && m.rank !== memberRankFilter) return false;
    if (memberMislFilter !== 'All' && m.misl !== memberMislFilter) return false;
    return true;
  });
  const filteredArtifacts = ARTIFACTS.filter(a => artifactRarityFilter === 'All' || a.rarity === artifactRarityFilter);
  const dropTotal = dropWeights.founders + dropWeights.rare + dropWeights.epic + dropWeights.standard;
  const renderContent = () => {
    switch (activeTab) {
      // ============================================================
      // OVERVIEW
      // ============================================================
      case 'overview':
        return <div className="space-y-6" data-id="element-2247">
            <SectionHeader title="System Overview" eyebrow="Real-time Treasury Metrics" action={<button className="px-4 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 transition-colors flex items-center gap-2" data-id="element-2249">
                  <Download className="w-3.5 h-3.5" data-id="element-2250" /> Export Report
                </button>} data-id="element-2248" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2251">
              <KpiTile label="Total Members" value="12,450" trend="+145 wk" up data-id="element-2252" />
              <KpiTile label="Active (24h)" value="3,820" trend="+12%" up data-id="element-2253" />
              <KpiTile label="Packets Today" value="2,140" trend="+8%" up data-id="element-2254" />
              <KpiTile label="Trade Volume 7d" value="1,240" trend="-5%" up={false} data-id="element-2255" />
              <KpiTile label="Total XP Issued" value="4.2M" trend="+220K" up data-id="element-2256" />
              <KpiTile label="Codes Redeemed" value="2,071" trend="+142" up data-id="element-2257" />
              <KpiTile label="Active Misls" value="20 / 20" trend="All live" up data-id="element-2258" />
              <KpiTile label="Flagged Trades" value="3" trend="Review" up={false} data-id="element-2259" />
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2260">
              <div className="flex items-end justify-between mb-4" data-id="element-2261">
                <div data-id="element-2262">
                  <h3 className="text-[#FDFBF7] font-serif text-lg leading-none" data-id="element-2263">
                    30-Day Activity
                  </h3>
                  <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mt-1.5" data-id="element-2264">
                    Daily Active Members
                  </p>
                </div>
                <div className="flex gap-2" data-id="element-2265">
                  {['7D', '30D', '90D', 'All'].map(p => <button key={p} className={`px-2.5 py-1 text-[10px] uppercase tracking-widest border ${p === '30D' ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/20 text-[#FDFBF7]/40 hover:text-[#FDFBF7]/70'}`} data-id="element-2266">
                      {p}
                    </button>)}
                </div>
              </div>
              <ActivityChart data={ACTIVITY_30D} data-id="element-2267" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-2268">
              <div className="lg:col-span-2 bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2269">
                <div className="flex items-center justify-between mb-4" data-id="element-2270">
                  <h3 className="text-[#FDFBF7] font-serif text-lg leading-none" data-id="element-2271">
                    Top Misls
                  </h3>
                  <button onClick={() => setActiveTab('misls')} className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-[10px] uppercase tracking-widest" data-id="element-2272">
                    Manage All →
                  </button>
                </div>
                <div className="divide-y divide-[#D4AF37]/10" data-id="element-2273">
                  {TOP_MISLS.map((m, i) => <div key={m.name} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0" data-id="element-2274">
                      <span className="font-mono text-[#D4AF37]/70 text-sm w-6" data-id="element-2275">
                        #{i + 1}
                      </span>
                      <div className="w-8 h-8 border border-[#D4AF37]/40 bg-[#0B0F19] flex items-center justify-center shrink-0" data-id="element-2276">
                        <Shield className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2277" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2278">
                        <p className="text-[#FDFBF7] text-sm leading-tight truncate" data-id="element-2279">
                          {m.name}
                        </p>
                        <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest mt-0.5" data-id="element-2280">
                          {m.category}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-2281">
                        <p className="text-[#D4AF37] text-xs font-mono" data-id="element-2282">
                          {m.xp} XP
                        </p>
                        <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-2283">
                          {m.members.toLocaleString()} members
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>

              <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2284">
                <h3 className="text-[#FDFBF7] font-serif text-lg mb-4" data-id="element-2285">
                  System Health
                </h3>
                <div className="space-y-3.5" data-id="element-2286">
                  {[{
                  k: 'API Status',
                  v: 'Operational',
                  tone: 'emerald'
                }, {
                  k: 'Packet RNG Integrity',
                  v: '✓ Verified',
                  tone: 'emerald'
                }, {
                  k: 'Database Load',
                  v: '24%',
                  tone: 'gold'
                }, {
                  k: 'Active Events',
                  v: '2 Live',
                  tone: 'gold'
                }, {
                  k: 'Trade Queue',
                  v: '0 backlog',
                  tone: 'emerald'
                }, {
                  k: 'Flagged Trades',
                  v: '3 Pending',
                  tone: 'red'
                }].map((row, i) => <div key={i} className="flex items-center justify-between" data-id="element-2287">
                      <span className="text-[#FDFBF7]/60 text-xs" data-id="element-2288">{row.k}</span>
                      <span className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${row.tone === 'emerald' ? 'text-emerald-400' : row.tone === 'red' ? 'text-red-400' : 'text-[#D4AF37]'}`} data-id="element-2289">
                        {row.tone === 'emerald' && <CheckCircle2 className="w-3 h-3" data-id="element-2290" />}
                        {row.tone === 'red' && <AlertCircle className="w-3 h-3" data-id="element-2291" />}
                        {row.v}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2292">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-4" data-id="element-2293">
                Live Activity Stream
              </h3>
              <div className="divide-y divide-[#D4AF37]/10" data-id="element-2294">
                {[{
                user: '@ranjit_s',
                action: 'opened a Daily Packet · pulled Royal Seal',
                time: '2m ago',
                type: 'packet'
              }, {
                user: '@harjeet_k',
                action: 'completed Founders Set (6/6)',
                time: '15m ago',
                type: 'collection'
              }, {
                user: '@simran_k',
                action: 'redeemed code COURT-AWAKEN-7B (+200 XP)',
                time: '38m ago',
                type: 'code'
              }, {
                user: '@arjan_s',
                action: 'joined Sukerchakia Misl',
                time: '1h ago',
                type: 'misl'
              }, {
                user: '@dilraj_s',
                action: 'traded Koh-i-Noor Diamond for 3 cards',
                time: '2h ago',
                type: 'trade'
              }].map((act, i) => <div key={i} className="flex items-center justify-between py-3 first:pt-0 last:pb-0" data-id="element-2295">
                    <div className="flex items-center gap-3" data-id="element-2296">
                      <div className="w-8 h-8 rounded-full bg-[#0B0F19] border border-[#D4AF37]/30 flex items-center justify-center" data-id="element-2297">
                        {act.type === 'packet' && <Gift className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2298" />}
                        {act.type === 'collection' && <Layers className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2299" />}
                        {act.type === 'trade' && <Repeat className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2300" />}
                        {act.type === 'misl' && <Shield className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2301" />}
                        {act.type === 'code' && <KeyRound className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2302" />}
                      </div>
                      <p className="text-sm text-[#FDFBF7]/80" data-id="element-2303">
                        <span className="text-[#D4AF37] font-medium" data-id="element-2304">
                          {act.user}
                        </span>{' '}
                        {act.action}
                      </p>
                    </div>
                    <span className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest shrink-0" data-id="element-2305">
                      {act.time}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>;
      // ============================================================
      // MEMBERS
      // ============================================================
      case 'members':
        return <div className="space-y-6" data-id="element-2306">
            <SectionHeader title="Member Directory" eyebrow={`${MEMBERS.length} members · ${filteredMembers.length} shown`} action={<>
                  <div className="relative" data-id="element-2308">
                    <Search className="w-4 h-4 text-[#D4AF37]/50 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2309" />
                    <input type="text" placeholder="Search by handle or email..." className="pl-9 pr-4 py-2 bg-[#131B2B] border border-[#D4AF37]/30 text-[#FDFBF7] text-xs focus:outline-none focus:border-[#D4AF37] w-64" data-id="element-2310" />
                  </div>
                  <button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2311">
                    <Plus className="w-3.5 h-3.5" data-id="element-2312" /> Invite Member
                  </button>
                </>} data-id="element-2307" />

            <div className="flex flex-wrap gap-3 items-center bg-[#131B2B] border border-[#D4AF37]/20 p-4" data-id="element-2313">
              <span className="text-[#D4AF37]/60 text-[10px] uppercase tracking-widest flex items-center gap-2" data-id="element-2314">
                <Filter className="w-3.5 h-3.5" data-id="element-2315" /> Filter
              </span>
              <select value={memberRankFilter} onChange={e => setMemberRankFilter(e.target.value)} className="bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-xs px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2316">
                <option value="All" data-id="element-2317">All Ranks</option>
                {ranks.map(r => <option key={r} value={r} data-id="element-2318">
                    {r}
                  </option>)}
              </select>
              <select value={memberMislFilter} onChange={e => setMemberMislFilter(e.target.value)} className="bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-xs px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2319">
                <option value="All" data-id="element-2320">All Misls</option>
                {allMisls.map(m => <option key={m} value={m} data-id="element-2321">
                    {m}
                  </option>)}
              </select>
              {(memberRankFilter !== 'All' || memberMislFilter !== 'All') && <button onClick={() => {
              setMemberRankFilter('All');
              setMemberMislFilter('All');
            }} className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-[10px] uppercase tracking-widest" data-id="element-2322">
                  Clear filters
                </button>}
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2323">
              <table className="w-full text-left border-collapse" data-id="element-2324">
                <thead data-id="element-2325">
                  <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2326">
                    <th className="p-4 font-normal" data-id="element-2327">Member</th>
                    <th className="p-4 font-normal" data-id="element-2328">Rank</th>
                    <th className="p-4 font-normal" data-id="element-2329">Misl</th>
                    <th className="p-4 font-normal" data-id="element-2330">XP</th>
                    <th className="p-4 font-normal" data-id="element-2331">Status</th>
                    <th className="p-4 font-normal" data-id="element-2332">Joined</th>
                    <th className="p-4 font-normal text-right" data-id="element-2333">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2334">
                  {filteredMembers.map(user => <tr key={user.handle} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2335">
                      <td className="p-4" data-id="element-2336">
                        <div className="flex items-center gap-3" data-id="element-2337">
                          <div className="w-8 h-8 rounded-full bg-[#0B0F19] border border-[#D4AF37]/30 flex items-center justify-center" data-id="element-2338">
                            <span className="text-[#D4AF37] text-xs font-serif" data-id="element-2339">
                              {user.name.charAt(0)}
                            </span>
                          </div>
                          <div data-id="element-2340">
                            <p className="text-[#FDFBF7] text-sm font-medium leading-tight" data-id="element-2341">
                              {user.name}
                            </p>
                            <p className="text-[#FDFBF7]/40 text-[10px] font-mono mt-0.5" data-id="element-2342">
                              {user.handle}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-[#D4AF37] text-xs" data-id="element-2343">
                        {user.rank}
                      </td>
                      <td className="p-4 text-[#FDFBF7]/70 text-xs" data-id="element-2344">
                        {user.misl}
                      </td>
                      <td className="p-4 text-[#D4AF37] text-xs font-mono" data-id="element-2345">
                        {user.xp}
                      </td>
                      <td className="p-4" data-id="element-2346">
                        <StatusPill status={user.status} data-id="element-2347" />
                      </td>
                      <td className="p-4 text-[#FDFBF7]/50 text-xs" data-id="element-2348">
                        {user.joined}
                      </td>
                      <td className="p-4 text-right" data-id="element-2349">
                        <div className="flex gap-2 justify-end" data-id="element-2350">
                          <button className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-[9px] uppercase tracking-widest border border-[#D4AF37]/30 px-2 py-1 hover:bg-[#D4AF37]/10 transition-colors" data-id="element-2351">
                            Grant XP
                          </button>
                          <button className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] text-[9px] uppercase tracking-widest border border-[#FDFBF7]/20 px-2 py-1 hover:bg-[#FDFBF7]/5 transition-colors" data-id="element-2352">
                            Promote
                          </button>
                          <button className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors p-1" data-id="element-2353">
                            <MoreVertical className="w-4 h-4" data-id="element-2354" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                  {filteredMembers.length === 0 && <tr data-id="element-2355">
                      <td colSpan={7} className="p-12 text-center text-[#FDFBF7]/40 text-sm" data-id="element-2356">
                        No members match the current filters.
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>;
      // ============================================================
      // MISLS
      // ============================================================
      case 'misls':
        return <div className="space-y-6" data-id="element-2357">
            <SectionHeader title="Misl Management" eyebrow="20 Misls across 4 categories" action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2359">
                  <Plus className="w-3.5 h-3.5" data-id="element-2360" /> Add New Misl
                </button>} data-id="element-2358" />

            {Object.entries(MISL_CATEGORIES).map(([category, data]) => <div key={category} className="space-y-3" data-id="element-2361">
                <div className="flex items-center justify-between border-l-2 pl-4" style={{
              borderColor: data.color
            }} data-id="element-2362">
                  <div data-id="element-2363">
                    <h3 className="font-serif text-xl text-[#FDFBF7] leading-tight" data-id="element-2364">
                      {category}
                    </h3>
                    <p className="text-[10px] tracking-[0.3em] uppercase mt-1" style={{
                  color: data.color + 'cc'
                }} data-id="element-2365">
                      {data.misls.length} Misls ·{' '}
                      {data.misls.reduce((s, m) => s + m.members, 0).toLocaleString()}{' '}
                      members
                    </p>
                  </div>
                </div>

                <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2366">
                  <table className="w-full text-left border-collapse" data-id="element-2367">
                    <thead data-id="element-2368">
                      <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2369">
                        <th className="p-4 font-normal" data-id="element-2370">Misl</th>
                        <th className="p-4 font-normal" data-id="element-2371">Region</th>
                        <th className="p-4 font-normal" data-id="element-2372">Members</th>
                        <th className="p-4 font-normal" data-id="element-2373">Misl XP</th>
                        <th className="p-4 font-normal" data-id="element-2374">Misldar</th>
                        <th className="p-4 font-normal text-right" data-id="element-2375">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2376">
                      {data.misls.map(m => <tr key={m.name} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2377">
                          <td className="p-4" data-id="element-2378">
                            <div className="flex items-center gap-3" data-id="element-2379">
                              <div className="w-8 h-8 border border-[#D4AF37]/40 bg-[#0B0F19] flex items-center justify-center" data-id="element-2380">
                                <Shield className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2381" />
                              </div>
                              <span className="text-[#FDFBF7] text-sm font-medium" data-id="element-2382">
                                {m.name}
                              </span>
                            </div>
                          </td>
                          <td className="p-4 text-[#FDFBF7]/70 text-xs" data-id="element-2383">
                            {m.region}
                          </td>
                          <td className="p-4 text-[#FDFBF7]/80 text-xs font-mono" data-id="element-2384">
                            {m.members.toLocaleString()}
                          </td>
                          <td className="p-4 text-[#D4AF37] text-xs font-mono" data-id="element-2385">
                            {m.xp}
                          </td>
                          <td className="p-4" data-id="element-2386">
                            <div className="flex items-center gap-2" data-id="element-2387">
                              <Crown className="w-3 h-3 text-[#D4AF37]" data-id="element-2388" />
                              <span className="text-[#D4AF37] text-[10px] font-mono" data-id="element-2389">
                                {m.misldar}
                              </span>
                            </div>
                          </td>
                          <td className="p-4 text-right" data-id="element-2390">
                            <div className="flex gap-2 justify-end" data-id="element-2391">
                              <button className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-[9px] uppercase tracking-widest border border-[#D4AF37]/30 px-2 py-1 hover:bg-[#D4AF37]/10 transition-colors flex items-center gap-1.5" data-id="element-2392">
                                <Crown className="w-3 h-3" data-id="element-2393" /> Set Misldar
                              </button>
                              <button className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] text-[9px] uppercase tracking-widest border border-[#FDFBF7]/20 px-2 py-1 hover:bg-[#FDFBF7]/5 transition-colors flex items-center gap-1.5" data-id="element-2394">
                                <Edit3 className="w-3 h-3" data-id="element-2395" /> Edit
                              </button>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>)}
          </div>;
      // ============================================================
      // CODES
      // ============================================================
      case 'codes':
        return <div className="space-y-6" data-id="element-2396">
            <SectionHeader title="Redemption Code Registry" eyebrow={`${CODES.length} codes · ${CODES.reduce((s, c) => s + c.used, 0).toLocaleString()} redemptions logged`} action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2398">
                  <Plus className="w-3.5 h-3.5" data-id="element-2399" /> Generate Code
                </button>} data-id="element-2397" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2400">
              <KpiTile label="Active Codes" value={CODES.filter(c => c.status === 'Active').length.toString()} data-id="element-2401" />
              <KpiTile label="Scheduled" value={CODES.filter(c => c.status === 'Scheduled').length.toString()} data-id="element-2402" />
              <KpiTile label="Total Redemptions" value={CODES.reduce((s, c) => s + c.used, 0).toLocaleString()} data-id="element-2403" />
              <KpiTile label="XP Distributed" value={CODES.reduce((s, c) => s + c.used * c.xp, 0).toLocaleString()} data-id="element-2404" />
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2405">
              <table className="w-full text-left border-collapse" data-id="element-2406">
                <thead data-id="element-2407">
                  <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2408">
                    <th className="p-4 font-normal" data-id="element-2409">Code</th>
                    <th className="p-4 font-normal" data-id="element-2410">Event</th>
                    <th className="p-4 font-normal" data-id="element-2411">XP</th>
                    <th className="p-4 font-normal" data-id="element-2412">Usage</th>
                    <th className="p-4 font-normal" data-id="element-2413">Expires</th>
                    <th className="p-4 font-normal" data-id="element-2414">Status</th>
                    <th className="p-4 font-normal text-right" data-id="element-2415">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2416">
                  {CODES.map(c => {
                  const pct = Math.round(c.used / c.total * 100);
                  return <tr key={c.code} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2417">
                        <td className="p-4" data-id="element-2418">
                          <span className="font-mono text-[#D4AF37] text-xs tracking-wider" data-id="element-2419">
                            {c.code}
                          </span>
                        </td>
                        <td className="p-4 text-[#FDFBF7]/80 text-xs" data-id="element-2420">
                          {c.event}
                        </td>
                        <td className="p-4 text-[#D4AF37] text-xs font-mono" data-id="element-2421">
                          +{c.xp}
                        </td>
                        <td className="p-4 w-44" data-id="element-2422">
                          <div className="flex items-center gap-2" data-id="element-2423">
                            <div className="flex-1 h-1.5 bg-[#0B0F19] border border-[#D4AF37]/20 overflow-hidden" data-id="element-2424">
                              <div className="h-full bg-[#D4AF37]" style={{
                            width: `${pct}%`
                          }} data-id="element-2425" />
                            </div>
                            <span className="text-[#FDFBF7]/60 text-[10px] font-mono whitespace-nowrap" data-id="element-2426">
                              {c.used}/{c.total}
                            </span>
                          </div>
                        </td>
                        <td className="p-4 text-[#FDFBF7]/60 text-xs" data-id="element-2427">
                          {c.expires}
                        </td>
                        <td className="p-4" data-id="element-2428">
                          <StatusPill status={c.status} data-id="element-2429" />
                        </td>
                        <td className="p-4 text-right" data-id="element-2430">
                          <div className="flex gap-2 justify-end" data-id="element-2431">
                            <button title="Copy code" className="text-[#D4AF37]/70 hover:text-[#D4AF37] p-1.5 border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 transition-colors" data-id="element-2432">
                              <Copy className="w-3 h-3" data-id="element-2433" />
                            </button>
                            <button title="Edit" className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] p-1.5 border border-[#FDFBF7]/20 hover:bg-[#FDFBF7]/5 transition-colors" data-id="element-2434">
                              <Edit3 className="w-3 h-3" data-id="element-2435" />
                            </button>
                            <button title="Revoke" className="text-red-400/70 hover:text-red-400 p-1.5 border border-red-400/30 hover:bg-red-400/10 transition-colors" data-id="element-2436">
                              <Trash2 className="w-3 h-3" data-id="element-2437" />
                            </button>
                          </div>
                        </td>
                      </tr>;
                })}
                </tbody>
              </table>
            </div>
          </div>;
      // ============================================================
      // COLLECTIBLES
      // ============================================================
      case 'collectibles':
        return <div className="space-y-6" data-id="element-2438">
            <SectionHeader title="Collectibles Catalog" eyebrow={`${ARTIFACTS.length} artifacts · ${filteredArtifacts.length} shown`} action={<>
                  <div className="relative" data-id="element-2440">
                    <Search className="w-4 h-4 text-[#D4AF37]/50 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2441" />
                    <input type="text" placeholder="Search artifacts..." className="pl-9 pr-4 py-2 bg-[#131B2B] border border-[#D4AF37]/30 text-[#FDFBF7] text-xs focus:outline-none focus:border-[#D4AF37] w-56" data-id="element-2442" />
                  </div>
                  <select value={artifactRarityFilter} onChange={e => setArtifactRarityFilter(e.target.value)} className="bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-xs px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2443">
                    <option value="All" data-id="element-2444">All Rarities</option>
                    {rarities.map(r => <option key={r} value={r} data-id="element-2445">
                        {r}
                      </option>)}
                  </select>
                  <button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2446">
                    <Plus className="w-3.5 h-3.5" data-id="element-2447" /> Add Artifact
                  </button>
                </>} data-id="element-2439" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2448">
              <KpiTile label="Total Artifacts" value={ARTIFACTS.length.toString()} data-id="element-2449" />
              <KpiTile label="Live" value={ARTIFACTS.filter(a => a.status === 'Live').length.toString()} data-id="element-2450" />
              <KpiTile label="Draft" value={ARTIFACTS.filter(a => a.status === 'Draft').length.toString()} data-id="element-2451" />
              <KpiTile label="Total Supply" value={ARTIFACTS.reduce((s, a) => s + a.supply, 0).toLocaleString()} data-id="element-2452" />
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2453">
              <table className="w-full text-left border-collapse" data-id="element-2454">
                <thead data-id="element-2455">
                  <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2456">
                    <th className="p-4 font-normal" data-id="element-2457">Artifact</th>
                    <th className="p-4 font-normal" data-id="element-2458">Rarity</th>
                    <th className="p-4 font-normal" data-id="element-2459">Collection</th>
                    <th className="p-4 font-normal" data-id="element-2460">Supply</th>
                    <th className="p-4 font-normal" data-id="element-2461">Owned</th>
                    <th className="p-4 font-normal" data-id="element-2462">Status</th>
                    <th className="p-4 font-normal text-right" data-id="element-2463">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2464">
                  {filteredArtifacts.map(a => {
                  const pct = a.supply > 0 ? Math.round(a.minted / a.supply * 100) : 0;
                  return <tr key={a.name} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2465">
                        <td className="p-4" data-id="element-2466">
                          <div className="flex items-center gap-3" data-id="element-2467">
                            <div className="w-10 h-10 bg-[#0B0F19] border border-[#D4AF37]/30 flex items-center justify-center overflow-hidden" data-id="element-2468">
                              {a.img ? <img src={a.img} alt={a.name} className="w-full h-full object-cover" data-id="element-2469" /> : <ImageIcon className="w-4 h-4 text-[#D4AF37]/50" data-id="element-2470" />}
                            </div>
                            <span className="text-[#FDFBF7] text-sm font-medium" data-id="element-2471">
                              {a.name}
                            </span>
                          </div>
                        </td>
                        <td className="p-4" data-id="element-2472">
                          <RarityChip rarity={a.rarity} data-id="element-2473" />
                        </td>
                        <td className="p-4 text-[#FDFBF7]/70 text-xs" data-id="element-2474">
                          {a.set}
                        </td>
                        <td className="p-4 text-[#FDFBF7]/80 text-xs font-mono" data-id="element-2475">
                          {a.minted.toLocaleString()} /{' '}
                          {a.supply.toLocaleString()}
                        </td>
                        <td className="p-4 w-32" data-id="element-2476">
                          <div className="flex items-center gap-2" data-id="element-2477">
                            <div className="flex-1 h-1.5 bg-[#0B0F19] border border-[#D4AF37]/20 overflow-hidden" data-id="element-2478">
                              <div className="h-full bg-[#D4AF37]" style={{
                            width: `${pct}%`
                          }} data-id="element-2479" />
                            </div>
                            <span className="text-[#FDFBF7]/60 text-[10px] font-mono" data-id="element-2480">
                              {pct}%
                            </span>
                          </div>
                        </td>
                        <td className="p-4" data-id="element-2481">
                          <StatusPill status={a.status} data-id="element-2482" />
                        </td>
                        <td className="p-4 text-right" data-id="element-2483">
                          <div className="flex gap-2 justify-end" data-id="element-2484">
                            <button className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] p-1.5 border border-[#FDFBF7]/20 hover:bg-[#FDFBF7]/5 transition-colors" data-id="element-2485">
                              <Edit3 className="w-3 h-3" data-id="element-2486" />
                            </button>
                            <button className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors p-1" data-id="element-2487">
                              <MoreVertical className="w-4 h-4" data-id="element-2488" />
                            </button>
                          </div>
                        </td>
                      </tr>;
                })}
                </tbody>
              </table>
            </div>
          </div>;
      // ============================================================
      // COLLECTIONS
      // ============================================================
      case 'collections':
        return <div className="space-y-6" data-id="element-2489">
            <SectionHeader title="Collection Sets" eyebrow={`${COLLECTIONS.length} sets · ${COLLECTIONS.filter(c => c.status === 'Live').length} live`} action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2491">
                  <Plus className="w-3.5 h-3.5" data-id="element-2492" /> New Collection
                </button>} data-id="element-2490" />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2493">
              {COLLECTIONS.map(c => {
              const pct = Math.round(c.complete / c.cards * 100);
              return <div key={c.name} className="bg-[#131B2B] border border-[#D4AF37]/20 p-5 flex flex-col gap-4" data-id="element-2494">
                    <div className="flex items-start justify-between" data-id="element-2495">
                      <div className="w-10 h-10 border border-[#D4AF37]/40 bg-[#0B0F19] flex items-center justify-center" data-id="element-2496">
                        <Layers className="w-4 h-4 text-[#D4AF37]" data-id="element-2497" />
                      </div>
                      <StatusPill status={c.status} data-id="element-2498" />
                    </div>
                    <div data-id="element-2499">
                      <h4 className="font-serif text-lg text-[#FDFBF7] leading-tight" data-id="element-2500">
                        {c.name}
                      </h4>
                      <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.25em] uppercase mt-1" data-id="element-2501">
                        {c.rarity} · Released {c.released}
                      </p>
                    </div>
                    <div data-id="element-2502">
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest mb-1.5" data-id="element-2503">
                        <span className="text-[#FDFBF7]/50" data-id="element-2504">Cards Live</span>
                        <span className="text-[#D4AF37] font-mono" data-id="element-2505">
                          {c.complete} / {c.cards}
                        </span>
                      </div>
                      <div className="h-1.5 bg-[#0B0F19] border border-[#D4AF37]/20 overflow-hidden" data-id="element-2506">
                        <div className="h-full bg-[#D4AF37]" style={{
                      width: `${pct}%`
                    }} data-id="element-2507" />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2 border-t border-[#D4AF37]/10" data-id="element-2508">
                      <button className="flex-1 text-[#D4AF37] text-[10px] uppercase tracking-widest border border-[#D4AF37]/30 px-3 py-2 hover:bg-[#D4AF37]/10 transition-colors flex items-center justify-center gap-1.5" data-id="element-2509">
                        <Edit3 className="w-3 h-3" data-id="element-2510" /> Edit
                      </button>
                      <button className="flex-1 text-[#FDFBF7]/60 text-[10px] uppercase tracking-widest border border-[#FDFBF7]/20 px-3 py-2 hover:bg-[#FDFBF7]/5 transition-colors flex items-center justify-center gap-1.5" data-id="element-2511">
                        <Eye className="w-3 h-3" data-id="element-2512" /> View
                      </button>
                    </div>
                  </div>;
            })}
            </div>
          </div>;
      // ============================================================
      // LIVE EVENTS
      // ============================================================
      case 'events':
        return <div className="space-y-6" data-id="element-2513">
            <SectionHeader title="Live Events Calendar" eyebrow={`${EVENTS_NIGHTS.length + EVENTS_SESSIONS.length} events scheduled`} action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2515">
                  <Plus className="w-3.5 h-3.5" data-id="element-2516" /> Schedule Event
                </button>} data-id="element-2514" />

            <div className="flex gap-2" data-id="element-2517">
              <button onClick={() => setEventSubTab('nights')} className={`px-4 py-2.5 text-[10px] uppercase tracking-widest border flex items-center gap-2 transition-colors ${eventSubTab === 'nights' ? 'border-[#8B0000] text-[#8B0000] bg-[#8B0000]/10' : 'border-[#D4AF37]/20 text-[#FDFBF7]/50 hover:text-[#FDFBF7]'}`} data-id="element-2518">
                <MapPin className="w-3.5 h-3.5" data-id="element-2519" /> Treasury Nights (
                {EVENTS_NIGHTS.length})
              </button>
              <button onClick={() => setEventSubTab('sessions')} className={`px-4 py-2.5 text-[10px] uppercase tracking-widest border flex items-center gap-2 transition-colors ${eventSubTab === 'sessions' ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/20 text-[#FDFBF7]/50 hover:text-[#FDFBF7]'}`} data-id="element-2520">
                <Video className="w-3.5 h-3.5" data-id="element-2521" /> Court Sessions (
                {EVENTS_SESSIONS.length})
              </button>
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2522">
              <table className="w-full text-left border-collapse" data-id="element-2523">
                <thead data-id="element-2524">
                  <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2525">
                    <th className="p-4 font-normal" data-id="element-2526">Date</th>
                    <th className="p-4 font-normal" data-id="element-2527">Event</th>
                    <th className="p-4 font-normal" data-id="element-2528">Location</th>
                    <th className="p-4 font-normal" data-id="element-2529">Rank Gate</th>
                    <th className="p-4 font-normal" data-id="element-2530">Code</th>
                    <th className="p-4 font-normal" data-id="element-2531">Attendance</th>
                    <th className="p-4 font-normal" data-id="element-2532">Status</th>
                    <th className="p-4 font-normal text-right" data-id="element-2533">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2534">
                  {(eventSubTab === 'nights' ? EVENTS_NIGHTS : EVENTS_SESSIONS).map(e => {
                  const pct = e.capacity > 0 ? Math.round(e.attended / e.capacity * 100) : 0;
                  return <tr key={e.code} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2535">
                        <td className="p-4 text-[#D4AF37] text-xs font-mono" data-id="element-2536">
                          {e.date}
                        </td>
                        <td className="p-4 text-[#FDFBF7] text-sm" data-id="element-2537">
                          {e.title}
                        </td>
                        <td className="p-4 text-[#FDFBF7]/70 text-xs" data-id="element-2538">
                          {e.location}
                        </td>
                        <td className="p-4 text-[#FDFBF7]/80 text-xs" data-id="element-2539">
                          {e.rank}
                        </td>
                        <td className="p-4" data-id="element-2540">
                          <span className="font-mono text-[#D4AF37] text-xs" data-id="element-2541">
                            {e.code}
                          </span>
                        </td>
                        <td className="p-4 w-36" data-id="element-2542">
                          <div className="flex items-center gap-2" data-id="element-2543">
                            <div className="flex-1 h-1.5 bg-[#0B0F19] border border-[#D4AF37]/20 overflow-hidden" data-id="element-2544">
                              <div className={`h-full ${eventSubTab === 'nights' ? 'bg-[#8B0000]' : 'bg-[#D4AF37]'}`} style={{
                            width: `${pct}%`
                          }} data-id="element-2545" />
                            </div>
                            <span className="text-[#FDFBF7]/60 text-[10px] font-mono" data-id="element-2546">
                              {e.attended}/{e.capacity}
                            </span>
                          </div>
                        </td>
                        <td className="p-4" data-id="element-2547">
                          <StatusPill status={e.status} data-id="element-2548" />
                        </td>
                        <td className="p-4 text-right" data-id="element-2549">
                          <div className="flex gap-2 justify-end" data-id="element-2550">
                            <button className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] p-1.5 border border-[#FDFBF7]/20 hover:bg-[#FDFBF7]/5 transition-colors" data-id="element-2551">
                              <Edit3 className="w-3 h-3" data-id="element-2552" />
                            </button>
                            <button className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors p-1" data-id="element-2553">
                              <MoreVertical className="w-4 h-4" data-id="element-2554" />
                            </button>
                          </div>
                        </td>
                      </tr>;
                })}
                </tbody>
              </table>
            </div>
          </div>;
      // ============================================================
      // TRADES
      // ============================================================
      case 'trades':
        return <div className="space-y-6" data-id="element-2555">
            <SectionHeader title="Trades & Open Market" eyebrow="Trade volume · Darbar fees · moderation" data-id="element-2556" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2557">
              <KpiTile label="Volume 7d" value="1,240" trend="-5%" up={false} data-id="element-2558" />
              <KpiTile label="Open Listings" value="412" data-id="element-2559" />
              <KpiTile label="Darbar Fees (7d)" value="1,240 XP" trend="+8%" up data-id="element-2560" />
              <KpiTile label="Flagged" value={TRADES_FLAGGED.length.toString()} trend="Review" up={false} data-id="element-2561" />
            </div>

            <div className="flex gap-2" data-id="element-2562">
              <button onClick={() => setTradeSubTab('recent')} className={`px-4 py-2.5 text-[10px] uppercase tracking-widest border transition-colors ${tradeSubTab === 'recent' ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/20 text-[#FDFBF7]/50 hover:text-[#FDFBF7]'}`} data-id="element-2563">
                Recent ({TRADES_RECENT.length})
              </button>
              <button onClick={() => setTradeSubTab('flagged')} className={`px-4 py-2.5 text-[10px] uppercase tracking-widest border flex items-center gap-2 transition-colors ${tradeSubTab === 'flagged' ? 'border-red-400 text-red-400 bg-red-400/10' : 'border-[#D4AF37]/20 text-[#FDFBF7]/50 hover:text-[#FDFBF7]'}`} data-id="element-2564">
                <Flag className="w-3.5 h-3.5" data-id="element-2565" /> Flagged (
                {TRADES_FLAGGED.length})
              </button>
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2566">
              <table className="w-full text-left border-collapse" data-id="element-2567">
                <thead data-id="element-2568">
                  <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2569">
                    <th className="p-4 font-normal" data-id="element-2570">Trade ID</th>
                    <th className="p-4 font-normal" data-id="element-2571">From → To</th>
                    <th className="p-4 font-normal" data-id="element-2572">Artifact</th>
                    <th className="p-4 font-normal" data-id="element-2573">Value</th>
                    <th className="p-4 font-normal" data-id="element-2574">Fee</th>
                    {tradeSubTab === 'flagged' && <th className="p-4 font-normal" data-id="element-2575">Reason</th>}
                    <th className="p-4 font-normal" data-id="element-2576">Time</th>
                    <th className="p-4 font-normal" data-id="element-2577">Status</th>
                    <th className="p-4 font-normal text-right" data-id="element-2578">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2579">
                  {(tradeSubTab === 'recent' ? TRADES_RECENT : TRADES_FLAGGED).map((t: any) => <tr key={t.id} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2580">
                      <td className="p-4 font-mono text-[#D4AF37] text-xs" data-id="element-2581">
                        {t.id}
                      </td>
                      <td className="p-4 text-[#FDFBF7]/80 text-xs" data-id="element-2582">
                        <span className="font-mono" data-id="element-2583">{t.from}</span>
                        <ArrowRight className="w-3 h-3 inline mx-1.5 text-[#D4AF37]/40" data-id="element-2584" />
                        <span className="font-mono" data-id="element-2585">{t.to}</span>
                      </td>
                      <td className="p-4 text-[#FDFBF7] text-xs" data-id="element-2586">
                        {t.artifact}
                      </td>
                      <td className="p-4 text-[#D4AF37] text-xs font-mono" data-id="element-2587">
                        {t.value}
                      </td>
                      <td className="p-4 text-[#FDFBF7]/60 text-xs font-mono" data-id="element-2588">
                        {t.fee}
                      </td>
                      {tradeSubTab === 'flagged' && <td className="p-4 text-red-400/80 text-xs" data-id="element-2589">
                          {t.reason}
                        </td>}
                      <td className="p-4 text-[#FDFBF7]/50 text-xs" data-id="element-2590">
                        {t.time}
                      </td>
                      <td className="p-4" data-id="element-2591">
                        <StatusPill status={t.status} data-id="element-2592" />
                      </td>
                      <td className="p-4 text-right" data-id="element-2593">
                        {tradeSubTab === 'flagged' ? <div className="flex gap-2 justify-end" data-id="element-2594">
                            <button className="text-emerald-400/80 hover:text-emerald-400 text-[9px] uppercase tracking-widest border border-emerald-400/30 px-2 py-1 hover:bg-emerald-400/10 transition-colors" data-id="element-2595">
                              Approve
                            </button>
                            <button className="text-red-400/80 hover:text-red-400 text-[9px] uppercase tracking-widest border border-red-400/30 px-2 py-1 hover:bg-red-400/10 transition-colors" data-id="element-2596">
                              Reverse
                            </button>
                          </div> : <button className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors p-1" data-id="element-2597">
                            <MoreVertical className="w-4 h-4" data-id="element-2598" />
                          </button>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>;
      // ============================================================
      // DAILY PACKET
      // ============================================================
      case 'packets':
        return <div className="space-y-6" data-id="element-2599">
            <SectionHeader title="Daily Packet Engine" eyebrow="Hero card · drop weights · open-rate" action={<button className="px-4 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 transition-colors flex items-center gap-2" data-id="element-2601">
                  <Plus className="w-3.5 h-3.5" data-id="element-2602" /> Issue Bonus Packet
                </button>} data-id="element-2600" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-2603">
              {/* Today's hero */}
              <div className="bg-[#131B2B] border border-[#D4AF37]/30 p-5 relative" data-id="element-2604">
                <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-2605" />
                <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-3" data-id="element-2606">
                  Today's Hero Card · Day 47
                </p>
                <div className="aspect-[4/5] bg-[#0B0F19] border border-[#D4AF37]/30 mb-4 overflow-hidden" data-id="element-2607">
                  <img src={RANJIT_IMG} alt="Maharaja Ranjit Singh" className="w-full h-full object-cover" data-id="element-2608" />
                </div>
                <h4 className="font-serif text-xl text-[#FDFBF7] leading-tight mb-1" data-id="element-2609">
                  Maharaja Ranjit Singh
                </h4>
                <RarityChip rarity="Founders' Edition" data-id="element-2610" />
                <div className="mt-4 grid grid-cols-2 gap-2 text-center" data-id="element-2611">
                  <div className="border border-[#D4AF37]/15 p-2" data-id="element-2612">
                    <p className="font-mono text-[#D4AF37] text-sm" data-id="element-2613">2,140</p>
                    <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest mt-0.5" data-id="element-2614">
                      Opened
                    </p>
                  </div>
                  <div className="border border-[#D4AF37]/15 p-2" data-id="element-2615">
                    <p className="font-mono text-[#D4AF37] text-sm" data-id="element-2616">82%</p>
                    <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest mt-0.5" data-id="element-2617">
                      Open Rate
                    </p>
                  </div>
                </div>
                <button className="mt-3 w-full px-3 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 transition-colors flex items-center justify-center gap-2" data-id="element-2618">
                  <Edit3 className="w-3 h-3" data-id="element-2619" /> Replace Hero
                </button>
              </div>

              {/* Drop weights */}
              <div className="lg:col-span-2 bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2620">
                <div className="flex items-end justify-between mb-4" data-id="element-2621">
                  <div data-id="element-2622">
                    <h3 className="text-[#FDFBF7] font-serif text-lg leading-none" data-id="element-2623">
                      Rarity Drop Weights
                    </h3>
                    <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mt-1.5" data-id="element-2624">
                      Must total 100%
                    </p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-mono ${dropTotal === 100 ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-2625">
                    Total: {dropTotal}%
                  </span>
                </div>
                <div className="space-y-4" data-id="element-2626">
                  {[{
                  key: 'founders' as const,
                  label: "Founders' Edition",
                  value: dropWeights.founders,
                  color: '#D4AF37'
                }, {
                  key: 'rare' as const,
                  label: 'Treasury Rare',
                  value: dropWeights.rare,
                  color: '#D4AF37'
                }, {
                  key: 'epic' as const,
                  label: 'Epic',
                  value: dropWeights.epic,
                  color: '#8B0000'
                }, {
                  key: 'standard' as const,
                  label: 'Standard',
                  value: dropWeights.standard,
                  color: '#FDFBF7'
                }].map(row => <div key={row.key} data-id="element-2627">
                      <div className="flex items-center justify-between text-xs mb-2" data-id="element-2628">
                        <span className="text-[#FDFBF7]/80" data-id="element-2629">{row.label}</span>
                        <div className="flex items-center gap-2" data-id="element-2630">
                          <input type="number" min={0} max={100} value={row.value} onChange={e => setDropWeights({
                        ...dropWeights,
                        [row.key]: Math.max(0, Math.min(100, Number(e.target.value) || 0))
                      })} className="w-16 bg-[#0B0F19] border border-[#D4AF37]/25 text-[#D4AF37] text-xs font-mono px-2 py-1 text-right focus:outline-none focus:border-[#D4AF37]" data-id="element-2631" />
                          <span className="text-[#D4AF37]/60 text-xs font-mono" data-id="element-2632">
                            %
                          </span>
                        </div>
                      </div>
                      <div className="h-2 bg-[#0B0F19] border border-[#D4AF37]/15 overflow-hidden" data-id="element-2633">
                        <div className="h-full transition-all" style={{
                      width: `${row.value}%`,
                      background: row.color,
                      opacity: 0.7
                    }} data-id="element-2634" />
                      </div>
                    </div>)}
                </div>
                <div className="flex gap-3 mt-6 pt-4 border-t border-[#D4AF37]/10" data-id="element-2635">
                  <button onClick={() => setDropWeights({
                  founders: 1,
                  rare: 8,
                  epic: 22,
                  standard: 69
                })} className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] text-[10px] uppercase tracking-widest border border-[#FDFBF7]/20 px-3 py-2 hover:bg-[#FDFBF7]/5 transition-colors flex items-center gap-2" data-id="element-2636">
                    <RotateCcw className="w-3 h-3" data-id="element-2637" /> Reset to default
                  </button>
                  <button disabled={dropTotal !== 100} className="ml-auto px-4 py-2 bg-[#D4AF37] disabled:bg-[#D4AF37]/30 disabled:cursor-not-allowed text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2638">
                    <Save className="w-3 h-3" data-id="element-2639" /> Save Weights
                  </button>
                </div>
              </div>
            </div>

            {/* Open-rate chart */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2640">
              <div className="flex items-end justify-between mb-4" data-id="element-2641">
                <div data-id="element-2642">
                  <h3 className="text-[#FDFBF7] font-serif text-lg leading-none" data-id="element-2643">
                    Open Rate Trend
                  </h3>
                  <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mt-1.5" data-id="element-2644">
                    14-day daily packet open rate (%)
                  </p>
                </div>
                <span className="text-emerald-400 text-[10px] tracking-widest uppercase flex items-center gap-1" data-id="element-2645">
                  <TrendingUp className="w-3 h-3" data-id="element-2646" /> +6% wk
                </span>
              </div>
              <ActivityChart data={OPENRATE_14D} height={140} data-id="element-2647" />
            </div>

            {/* Upcoming schedule */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2648">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-4" data-id="element-2649">
                Upcoming Hero Cards · Next 7 Days
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-7 gap-2" data-id="element-2650">
                {[{
                d: 'Tomorrow',
                card: 'Hari Singh Nalwa',
                r: 'Epic',
                img: NALWA_IMG
              }, {
                d: '+2d',
                card: 'Akali Phula Singh',
                r: 'Epic',
                img: NALWA_IMG
              }, {
                d: '+3d',
                card: 'Moran Sarkar',
                r: 'Epic',
                img: MORAN_IMG
              }, {
                d: '+4d',
                card: 'Koh-i-Noor',
                r: 'Treasury Rare',
                img: RANJIT_IMG
              }, {
                d: '+5d',
                card: 'Jind Kaur',
                r: 'Epic',
                img: JIND_IMG
              }, {
                d: '+6d',
                card: 'Sukerchakia Seal',
                r: 'Treasury Rare',
                img: RANJIT_IMG
              }, {
                d: '+7d',
                card: 'Maharaja Ranjit Singh',
                r: "Founders' Edition",
                img: RANJIT_IMG
              }].map(u => <div key={u.d} className="p-3 border border-[#D4AF37]/15 bg-[#0B0F19] text-center" data-id="element-2651">
                    <p className="text-[#D4AF37]/70 text-[10px] tracking-widest uppercase mb-2" data-id="element-2652">
                      {u.d}
                    </p>
                    <div className="w-full aspect-[4/5] bg-[#131B2B] border border-[#D4AF37]/20 mb-2 overflow-hidden flex items-center justify-center" data-id="element-2653">
                      {u.img ? <img src={u.img} alt={u.card} className="w-full h-full object-cover" data-id="element-2654" /> : <ImageIcon className="w-4 h-4 text-[#D4AF37]/30" data-id="element-2655" />}
                    </div>
                    <p className="text-[#FDFBF7] text-[11px] leading-tight mb-1" data-id="element-2656">
                      {u.card}
                    </p>
                    <p className="text-[#D4AF37]/60 text-[9px] uppercase tracking-widest" data-id="element-2657">
                      {u.r}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>;
      // ============================================================
      // QUESTS
      // ============================================================
      case 'quests':
        return <div className="space-y-6" data-id="element-2658">
            <SectionHeader title="XP Quests" eyebrow={`${QUESTS.length} quests · ${QUESTS.filter(q => q.status === 'Active').length} active`} action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2660">
                  <Plus className="w-3.5 h-3.5" data-id="element-2661" /> Create Quest
                </button>} data-id="element-2659" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2662">
              <KpiTile label="Active Quests" value={QUESTS.filter(q => q.status === 'Active').length.toString()} data-id="element-2663" />
              <KpiTile label="Avg Completion" value="32%" trend="+4%" up data-id="element-2664" />
              <KpiTile label="XP Awarded 7d" value="42,150" trend="+12%" up data-id="element-2665" />
              <KpiTile label="One-shot Quests" value={QUESTS.filter(q => q.cadence === 'One-shot').length.toString()} data-id="element-2666" />
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2667">
              <table className="w-full text-left border-collapse" data-id="element-2668">
                <thead data-id="element-2669">
                  <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2670">
                    <th className="p-4 font-normal" data-id="element-2671">Quest</th>
                    <th className="p-4 font-normal" data-id="element-2672">Trigger</th>
                    <th className="p-4 font-normal" data-id="element-2673">Reward</th>
                    <th className="p-4 font-normal" data-id="element-2674">Cadence</th>
                    <th className="p-4 font-normal" data-id="element-2675">Completion</th>
                    <th className="p-4 font-normal" data-id="element-2676">Status</th>
                    <th className="p-4 font-normal text-right" data-id="element-2677">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2678">
                  {QUESTS.map(q => <tr key={q.name} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2679">
                      <td className="p-4" data-id="element-2680">
                        <div className="flex items-center gap-3" data-id="element-2681">
                          <div className="w-8 h-8 border border-[#D4AF37]/40 bg-[#0B0F19] flex items-center justify-center" data-id="element-2682">
                            <Target className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2683" />
                          </div>
                          <span className="text-[#FDFBF7] text-sm font-medium" data-id="element-2684">
                            {q.name}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 text-[#FDFBF7]/70 text-xs" data-id="element-2685">
                        {q.trigger}
                      </td>
                      <td className="p-4 text-[#D4AF37] text-xs font-mono" data-id="element-2686">
                        {q.reward}
                      </td>
                      <td className="p-4" data-id="element-2687">
                        <span className="text-[#FDFBF7]/60 text-[10px] uppercase tracking-widest border border-[#FDFBF7]/15 px-2 py-1" data-id="element-2688">
                          {q.cadence}
                        </span>
                      </td>
                      <td className="p-4 w-36" data-id="element-2689">
                        <div className="flex items-center gap-2" data-id="element-2690">
                          <div className="flex-1 h-1.5 bg-[#0B0F19] border border-[#D4AF37]/20 overflow-hidden" data-id="element-2691">
                            <div className="h-full bg-[#D4AF37]" style={{
                          width: `${q.completion}%`
                        }} data-id="element-2692" />
                          </div>
                          <span className="text-[#FDFBF7]/60 text-[10px] font-mono" data-id="element-2693">
                            {q.completion}%
                          </span>
                        </div>
                      </td>
                      <td className="p-4" data-id="element-2694">
                        <StatusPill status={q.status} data-id="element-2695" />
                      </td>
                      <td className="p-4 text-right" data-id="element-2696">
                        <div className="flex gap-2 justify-end" data-id="element-2697">
                          <button className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] p-1.5 border border-[#FDFBF7]/20 hover:bg-[#FDFBF7]/5 transition-colors" data-id="element-2698">
                            <Edit3 className="w-3 h-3" data-id="element-2699" />
                          </button>
                          <button className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors p-1" data-id="element-2700">
                            <MoreVertical className="w-4 h-4" data-id="element-2701" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>;
      // ============================================================
      // REFERRALS
      // ============================================================
      case 'referrals':
        return <div className="space-y-6" data-id="element-2702">
            <SectionHeader title="Referrals & Royal Decrees" eyebrow="Top referrers · funnel · tier rewards" data-id="element-2703" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2704">
              <KpiTile label="Total Invites Sent" value="3,420" trend="+142" up data-id="element-2705" />
              <KpiTile label="Signups from Invites" value="1,820" trend="+82" up data-id="element-2706" />
              <KpiTile label="Conversion Rate" value="53%" trend="+3%" up data-id="element-2707" />
              <KpiTile label="Top Referrer XP" value="8,400" data-id="element-2708" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-id="element-2709">
              {/* Leaderboard */}
              <div className="lg:col-span-3 bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2710">
                <div className="flex items-center justify-between mb-4" data-id="element-2711">
                  <h3 className="text-[#FDFBF7] font-serif text-lg leading-none" data-id="element-2712">
                    Top Referrers
                  </h3>
                  <span className="text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2713">
                    All time
                  </span>
                </div>
                <div className="divide-y divide-[#D4AF37]/10" data-id="element-2714">
                  {REFERRERS.map(r => <div key={r.handle} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0" data-id="element-2715">
                      <span className={`w-7 text-center font-mono text-sm ${r.rank <= 3 ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/50'}`} data-id="element-2716">
                        #{r.rank}
                      </span>
                      <div className="w-8 h-8 rounded-full bg-[#0B0F19] border border-[#D4AF37]/30 flex items-center justify-center shrink-0" data-id="element-2717">
                        <span className="text-[#D4AF37] text-xs font-serif" data-id="element-2718">
                          {r.name.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2719">
                        <p className="text-[#FDFBF7] text-sm leading-tight truncate" data-id="element-2720">
                          {r.name}
                        </p>
                        <p className="text-[#FDFBF7]/40 text-[10px] font-mono mt-0.5 truncate" data-id="element-2721">
                          {r.handle}
                        </p>
                      </div>
                      <div className="text-right grid grid-cols-3 gap-4 text-xs" data-id="element-2722">
                        <div data-id="element-2723">
                          <p className="text-[#FDFBF7]/80 font-mono" data-id="element-2724">
                            {r.invites}
                          </p>
                          <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest" data-id="element-2725">
                            Invites
                          </p>
                        </div>
                        <div data-id="element-2726">
                          <p className="text-[#FDFBF7]/80 font-mono" data-id="element-2727">
                            {r.signups}
                          </p>
                          <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest" data-id="element-2728">
                            Signups
                          </p>
                        </div>
                        <div data-id="element-2729">
                          <p className="text-[#D4AF37] font-mono" data-id="element-2730">{r.xp}</p>
                          <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest" data-id="element-2731">
                            XP
                          </p>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Tier rewards */}
              <div className="lg:col-span-2 bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2732">
                <div className="flex items-center justify-between mb-4" data-id="element-2733">
                  <h3 className="text-[#FDFBF7] font-serif text-lg leading-none" data-id="element-2734">
                    Reward Tiers
                  </h3>
                  <button className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-[10px] uppercase tracking-widest" data-id="element-2735">
                    Edit
                  </button>
                </div>
                <div className="space-y-3" data-id="element-2736">
                  {TIER_REWARDS.map(t => <div key={t.count} className="flex items-center gap-3 p-3 border border-[#D4AF37]/15 bg-[#0B0F19]" data-id="element-2737">
                      <div className="w-10 h-10 border border-[#D4AF37]/40 bg-[#131B2B] flex items-center justify-center shrink-0" data-id="element-2738">
                        <span className="text-[#D4AF37] font-serif text-base" data-id="element-2739">
                          {t.count}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2740">
                        <p className="text-[#D4AF37] text-xs font-bold leading-tight" data-id="element-2741">
                          {t.reward}
                        </p>
                        <p className="text-[#FDFBF7]/50 text-[10px] mt-0.5 leading-tight" data-id="element-2742">
                          {t.label}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Funnel */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2743">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-4" data-id="element-2744">
                Referral Funnel · 30d
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-id="element-2745">
                {[{
                label: 'Invites Sent',
                value: '3,420',
                pct: 100
              }, {
                label: 'Link Clicks',
                value: '2,180',
                pct: 64
              }, {
                label: 'Signups',
                value: '1,820',
                pct: 53
              }, {
                label: 'Activated',
                value: '1,490',
                pct: 44
              }].map((step, i) => <div key={i} className="bg-[#0B0F19] border border-[#D4AF37]/15 p-4 flex flex-col gap-2" data-id="element-2746">
                    <p className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest" data-id="element-2747">
                      {step.label}
                    </p>
                    <p className="font-serif text-2xl text-[#D4AF37]" data-id="element-2748">
                      {step.value}
                    </p>
                    <div className="h-1 bg-[#131B2B] border border-[#D4AF37]/20 overflow-hidden" data-id="element-2749">
                      <div className="h-full bg-[#D4AF37]" style={{
                    width: `${step.pct}%`
                  }} data-id="element-2750" />
                    </div>
                    <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest" data-id="element-2751">
                      {step.pct}% of sent
                    </p>
                  </div>)}
              </div>
            </div>
          </div>;
      // ============================================================
      // AUDIT LOG
      // ============================================================
      case 'audit':
        return <div className="space-y-6" data-id="element-2752">
            <SectionHeader title="Audit Log" eyebrow={`${AUDIT_LOG.length} actions · last 7 days`} action={<>
                  <select className="bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-xs px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2754">
                    <option data-id="element-2755">All admins</option>
                    <option data-id="element-2756">@harnoor_s</option>
                    <option data-id="element-2757">@ranjit_s</option>
                  </select>
                  <select className="bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-xs px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2758">
                    <option data-id="element-2759">All actions</option>
                    <option data-id="element-2760">XP Grant</option>
                    <option data-id="element-2761">Event</option>
                    <option data-id="element-2762">Artifact</option>
                    <option data-id="element-2763">Misl</option>
                    <option data-id="element-2764">Trade</option>
                    <option data-id="element-2765">Settings</option>
                  </select>
                  <button className="px-4 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 transition-colors flex items-center gap-2" data-id="element-2766">
                    <Download className="w-3.5 h-3.5" data-id="element-2767" /> Export
                  </button>
                </>} data-id="element-2753" />

            <div className="bg-[#131B2B] border border-[#D4AF37]/20" data-id="element-2768">
              <div className="divide-y divide-[#D4AF37]/10" data-id="element-2769">
                {AUDIT_LOG.map((row, i) => <div key={i} className="flex flex-wrap items-center gap-3 px-5 py-4 hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2770">
                    <span className="font-mono text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest w-44 shrink-0" data-id="element-2771">
                      {row.ts}
                    </span>
                    <span className="px-2 py-0.5 text-[9px] uppercase tracking-widest border border-[#D4AF37]/30 text-[#D4AF37]/80 shrink-0" data-id="element-2772">
                      {row.kind}
                    </span>
                    <p className="text-[#FDFBF7]/80 text-sm flex-1 min-w-0" data-id="element-2773">
                      <span className="text-[#D4AF37] font-mono" data-id="element-2774">
                        {row.admin}
                      </span>{' '}
                      <span className="text-[#FDFBF7]/60" data-id="element-2775">{row.action}</span>{' '}
                      <span className="text-[#FDFBF7]" data-id="element-2776">{row.target}</span>
                    </p>
                  </div>)}
              </div>
            </div>
          </div>;
      // ============================================================
      // RESTORATION MANAGER
      // ============================================================
      case 'restoration':
        return <div className="space-y-6" data-id="element-2777">
            <SectionHeader title="Restoration Manager" eyebrow="Zones · Schools · Milestones" action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2779">
                  <Plus className="w-3.5 h-3.5" data-id="element-2780" /> New Zone
                </button>} data-id="element-2778" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2781">
              <div className="lg:col-span-2 space-y-6" data-id="element-2782">
                <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-6" data-id="element-2783">
                  <h3 className="text-[#FDFBF7] font-serif text-lg mb-4" data-id="element-2784">
                    Active Zones
                  </h3>
                  <div className="space-y-3" data-id="element-2785">
                    {['Lahore Court', 'Sacred Punjab', 'Frontier Defenders', 'Treasury Vault'].map((zone, i) => <div key={i} className="flex items-center justify-between p-3 bg-[#0B0F19] border border-white/5" data-id="element-2786">
                        <div data-id="element-2787">
                          <p className="text-[#FDFBF7] text-sm" data-id="element-2788">{zone}</p>
                          <p className="text-[#FDFBF7]/40 text-xs" data-id="element-2789">
                            Progress: {Math.floor(Math.random() * 80) + 10}%
                          </p>
                        </div>
                        <button className="text-[#D4AF37] text-xs hover:underline" data-id="element-2790">
                          Edit
                        </button>
                      </div>)}
                  </div>
                </div>
              </div>

              <div className="space-y-6" data-id="element-2791">
                <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-6" data-id="element-2792">
                  <h3 className="text-[#FDFBF7] font-serif text-lg mb-4" data-id="element-2793">
                    School Progress
                  </h3>
                  <div className="space-y-3" data-id="element-2794">
                    {['Bramalea Secondary', 'Khalsa Community'].map((school, i) => <div key={i} className="p-3 bg-[#0B0F19] border border-white/5" data-id="element-2795">
                          <p className="text-[#FDFBF7] text-sm" data-id="element-2796">{school}</p>
                          <p className="text-[#D4AF37] text-xs mt-1" data-id="element-2797">
                            {Math.floor(Math.random() * 500) + 100} pts
                          </p>
                        </div>)}
                  </div>
                  <button className="w-full mt-4 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-xs uppercase tracking-widest hover:bg-[#D4AF37]/10 transition-colors" data-id="element-2798">
                    View All Schools
                  </button>
                </div>
              </div>
            </div>
          </div>;
      // ============================================================
      // SETTINGS
      // ============================================================
      case 'settings':
        return <div className="space-y-6" data-id="element-2799">
            <SectionHeader title="System Settings" eyebrow="Brand · economy · feature flags" action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2801">
                  <Save className="w-3.5 h-3.5" data-id="element-2802" /> Save Changes
                </button>} data-id="element-2800" />

            {/* Brand Identity */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2803">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-1" data-id="element-2804">
                Brand Identity
              </h3>
              <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-5" data-id="element-2805">
                Public-facing name, tagline, hero copy
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2806">
                <div data-id="element-2807">
                  <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-2808">
                    Brand Name
                  </label>
                  <input type="text" defaultValue="Treasures of the Sikh Empire" className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-sm px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2809" />
                </div>
                <div data-id="element-2810">
                  <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-2811">
                    Tagline
                  </label>
                  <input type="text" defaultValue="Recovering the lost stories of the Sikh Kingdoms" className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-sm px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2812" />
                </div>
                <div className="md:col-span-2" data-id="element-2813">
                  <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-2814">
                    Hero CTA Label
                  </label>
                  <input type="text" defaultValue="Enter the Lahore Court" className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-sm px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2815" />
                </div>
              </div>
            </div>

            {/* Economy */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2816">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-1" data-id="element-2817">
                Economy
              </h3>
              <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-5" data-id="element-2818">
                XP values & Darbar fees
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2819">
                {[{
                label: 'XP per Daily Packet',
                value: '50'
              }, {
                label: 'XP per Code Redeem',
                value: '200'
              }, {
                label: 'Darbar Fee (XP)',
                value: '1'
              }, {
                label: 'Referral Bonus XP',
                value: '500'
              }].map(f => <div key={f.label} data-id="element-2820">
                    <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-2821">
                      {f.label}
                    </label>
                    <input type="number" defaultValue={f.value} className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#D4AF37] text-sm font-mono px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2822" />
                  </div>)}
              </div>
            </div>

            {/* Event code prefix */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2823">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-1" data-id="element-2824">
                Event Codes
              </h3>
              <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-5" data-id="element-2825">
                Format & generation rules
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2826">
                <div data-id="element-2827">
                  <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-2828">
                    Code Prefix
                  </label>
                  <input type="text" defaultValue="COURT-" className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#D4AF37] text-sm font-mono px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2829" />
                </div>
                <div data-id="element-2830">
                  <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-2831">
                    Code Length
                  </label>
                  <input type="number" defaultValue={12} className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#D4AF37] text-sm font-mono px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2832" />
                </div>
                <div data-id="element-2833">
                  <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-2834">
                    Default Expiry (days)
                  </label>
                  <input type="number" defaultValue={30} className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#D4AF37] text-sm font-mono px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2835" />
                </div>
              </div>
            </div>

            {/* Feature flags */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2836">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-1" data-id="element-2837">
                Feature Flags
              </h3>
              <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-5" data-id="element-2838">
                Toggle modules on or off
              </p>
              <div className="divide-y divide-[#D4AF37]/10" data-id="element-2839">
                {[{
                key: 'tradeMarket' as const,
                label: 'Open Trade Market',
                desc: 'Allow members to list and trade artifacts in the Open Market.'
              }, {
                key: 'courtSessions' as const,
                label: 'Court Sessions (Virtual)',
                desc: 'Enable virtual Court Session events on the Court Channel.'
              }, {
                key: 'mislSwitching' as const,
                label: 'Misl Switching',
                desc: 'Allow members to leave and join a different Misl.'
              }, {
                key: 'publicLeaderboard' as const,
                label: 'Public Leaderboard',
                desc: 'Show the global XP leaderboard to non-members.'
              }].map(f => <div key={f.key} className="flex items-center justify-between py-3 first:pt-0 last:pb-0 gap-4" data-id="element-2840">
                    <div className="flex-1 min-w-0" data-id="element-2841">
                      <p className="text-[#FDFBF7] text-sm leading-tight" data-id="element-2842">
                        {f.label}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-xs mt-0.5" data-id="element-2843">
                        {f.desc}
                      </p>
                    </div>
                    <Toggle on={featureFlags[f.key]} onChange={() => setFeatureFlags({
                  ...featureFlags,
                  [f.key]: !featureFlags[f.key]
                })} data-id="element-2844" />
                  </div>)}
              </div>
            </div>

            {/* Danger zone */}
            <div className="bg-[#131B2B] border border-red-400/30 p-5" data-id="element-2845">
              <h3 className="text-red-400 font-serif text-lg mb-1" data-id="element-2846">
                Danger Zone
              </h3>
              <p className="text-red-400/60 text-[10px] tracking-[0.2em] uppercase mb-5" data-id="element-2847">
                Irreversible actions
              </p>
              <div className="space-y-3" data-id="element-2848">
                {[{
                label: 'Reset Demo Data',
                desc: 'Restore all mock data to the initial state. Cannot be undone.'
              }, {
                label: 'Archive Current Season',
                desc: 'Close out the current set of collections and start a new season.'
              }, {
                label: 'Transfer Ownership',
                desc: 'Hand over Founder Admin privileges to another account.'
              }].map(d => <div key={d.label} className="flex items-center justify-between gap-4 p-4 border border-red-400/20 bg-red-400/[0.03]" data-id="element-2849">
                    <div className="flex-1 min-w-0" data-id="element-2850">
                      <p className="text-[#FDFBF7] text-sm leading-tight" data-id="element-2851">
                        {d.label}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-xs mt-0.5" data-id="element-2852">
                        {d.desc}
                      </p>
                    </div>
                    <button className="text-red-400 hover:text-red-300 text-[10px] uppercase tracking-widest border border-red-400/40 hover:bg-red-400/10 px-3 py-2 transition-colors shrink-0" data-id="element-2853">
                      {d.label.split(' ')[0]}
                    </button>
                  </div>)}
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  // ============================================================
  // INLINE VARIANT — renders inside a parent container (no overlay)
  // ============================================================
  if (inline) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="flex flex-col lg:flex-row border border-[#D4AF37]/20 bg-[#131B2B]/30 backdrop-blur-sm min-h-[700px] w-full" data-id="element-2854">
        {/* Admin Sidebar */}
        <div className="w-full lg:w-64 border-b lg:border-b-0 lg:border-r border-[#D4AF37]/20 flex lg:flex-col overflow-x-auto lg:overflow-x-visible lg:overflow-y-auto no-scrollbar shrink-0" data-id="element-2855">
          {/* Admin badge + exit */}
          <div className="hidden lg:flex items-center justify-between gap-2 px-5 py-4 border-b border-[#D4AF37]/15 bg-[#0B0F19]/50" data-id="element-2856">
            <div className="flex items-center gap-2 min-w-0" data-id="element-2857">
              <div className="w-7 h-7 border border-[#D4AF37] flex items-center justify-center bg-[#D4AF37]/10 shrink-0" data-id="element-2858">
                <Shield className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2859" />
              </div>
              <div className="min-w-0" data-id="element-2860">
                <p className="font-serif text-[13px] text-[#FDFBF7] leading-none truncate" data-id="element-2861">
                  Treasury Admin
                </p>
                <p className="text-[#D4AF37]/60 text-[8px] tracking-[0.2em] uppercase mt-1 truncate" data-id="element-2862">
                  Control Center
                </p>
              </div>
            </div>
            <button onClick={onClose} aria-label="Exit admin" title="Exit admin" className="w-7 h-7 flex items-center justify-center text-[#FDFBF7]/50 hover:text-[#FDFBF7] hover:bg-[#FDFBF7]/10 transition-colors shrink-0" data-id="element-2863">
              <X className="w-3.5 h-3.5" data-id="element-2864" />
            </button>
          </div>

          {/* Mobile exit button (inline with tab row) */}
          <button onClick={onClose} className="lg:hidden flex items-center gap-2 px-5 py-4 text-[#D4AF37] border-r border-[#D4AF37]/15 shrink-0" data-id="element-2865">
            <X className="w-4 h-4" data-id="element-2866" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-medium whitespace-nowrap" data-id="element-2867">
              Exit
            </span>
          </button>

          {tabs.map(tab => <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-3 px-5 py-4 lg:py-3 text-left transition-all relative shrink-0 lg:shrink ${activeTab === tab.id ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'text-[#FDFBF7]/40 hover:text-[#FDFBF7]/70 hover:bg-[#FDFBF7]/5'}`} data-id="element-2868">
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 lg:h-auto lg:right-auto lg:top-0 lg:bottom-0 lg:w-0.5 bg-[#D4AF37]" data-id="element-2869" />}
              <tab.icon className="w-4 h-4 shrink-0" data-id="element-2870" />
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-medium whitespace-nowrap" data-id="element-2871">
                {tab.label}
              </span>
            </button>)}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-5 sm:p-8 lg:p-10 overflow-y-auto max-h-[80vh] lg:max-h-[800px]" data-id="element-2872">
          {renderContent()}
        </div>
      </motion.div>;
  }
  // ============================================================
  // OVERLAY VARIANT — original full-screen modal
  // ============================================================
  return <AnimatePresence data-id="element-2873">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] bg-[#0B0F19] flex flex-col" data-id="element-2874">
        {/* Topbar */}
        <div className="h-16 border-b border-[#D4AF37]/20 flex items-center justify-between px-6 shrink-0 bg-[#0B0F19]" data-id="element-2875">
          <div className="flex items-center gap-4" data-id="element-2876">
            <div className="w-8 h-8 border border-[#D4AF37] flex items-center justify-center bg-[#D4AF37]/10" data-id="element-2877">
              <Shield className="w-4 h-4 text-[#D4AF37]" data-id="element-2878" />
            </div>
            <div data-id="element-2879">
              <h1 className="font-serif text-lg text-[#FDFBF7] leading-none" data-id="element-2880">
                Treasury Admin
              </h1>
              <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.2em] uppercase mt-1" data-id="element-2881">
                System Control Center
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-2882">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 border border-[#D4AF37]/30 bg-[#131B2B]" data-id="element-2883">
              <div className="w-6 h-6 rounded-full bg-[#0B0F19] border border-[#D4AF37]/40 flex items-center justify-center" data-id="element-2884">
                <span className="text-[#D4AF37] text-[10px] font-serif" data-id="element-2885">H</span>
              </div>
              <div className="text-left" data-id="element-2886">
                <p className="text-[#FDFBF7] text-[11px] leading-none" data-id="element-2887">
                  Harnoor S.
                </p>
                <p className="text-[#D4AF37]/60 text-[8px] tracking-widest uppercase mt-0.5" data-id="element-2888">
                  Founder Admin
                </p>
              </div>
            </div>
            <button onClick={onClose} aria-label="Close admin dashboard" className="w-9 h-9 flex items-center justify-center text-[#FDFBF7]/60 hover:text-[#FDFBF7] hover:bg-[#FDFBF7]/10 transition-colors" data-id="element-2889">
              <X className="w-5 h-5" data-id="element-2890" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden" data-id="element-2891">
          {/* Sidebar */}
          <div className="w-60 border-r border-[#D4AF37]/20 bg-[#131B2B]/50 overflow-y-auto py-4 flex flex-col gap-0.5 shrink-0" data-id="element-2892">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-3 px-6 py-3 text-sm transition-colors relative text-left ${activeTab === tab.id ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-[#FDFBF7]/60 hover:text-[#FDFBF7] hover:bg-[#FDFBF7]/5'}`} data-id="element-2893">
                {activeTab === tab.id && <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#D4AF37]" data-id="element-2894" />}
                <tab.icon className="w-4 h-4 shrink-0" data-id="element-2895" />
                <span className="truncate" data-id="element-2896">{tab.label}</span>
              </button>)}
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6 sm:p-8 bg-[#0B0F19]" data-id="element-2897">
            <div className="max-w-6xl mx-auto" data-id="element-2898">{renderContent()}</div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>;
};